// FeaturesSection.jsx
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./FeaturesSection.css";

const THREE_D_ASSET_BASE =
  "https://cdn.jsdelivr.net/gh/microsoft/fluentui-emoji@main/assets";

const getThreeDImage = (folder, fileName) =>
  `${THREE_D_ASSET_BASE}/${encodeURIComponent(
    folder
  )}/3D/${fileName}_3d.png`;

const features = [
  {
    title: "Food & Beverages",
    desc: "Delicious in-theatre dining options to satisfy every taste at affordable prices.",
    image: getThreeDImage("Popcorn", "popcorn"),
  },
  {
    title: "3D Private Theatre Screening",
    desc: "Watch your favorite movies and shows in stunning 3D on big HD screens with immersive surround sound.",
    image: getThreeDImage("Movie camera", "movie_camera"),
  },
  {
    title: "Cakes",
    desc: "Choose from custom-designed cakes, unique flavors, and themed designs.",
    image: getThreeDImage("Birthday cake", "birthday_cake"),
  },
  {
    title: "Bouquets & Floral Surprises",
    desc: "Add a beautiful bouquet or floral arrangement to elevate your special moment.",
    image: getThreeDImage("Bouquet", "bouquet"),
  },
  {
    title: "Gifts",
    desc: "Celebrate your loved one with unique chocolates and personalized gifting options.",
    image: getThreeDImage("Wrapped gift", "wrapped_gift"),
  },
  {
    title: "Decoration & Themed Setups",
    desc: "Personalized decor, balloons, and themed setups tailored to your occasion.",
    image: getThreeDImage("Party popper", "party_popper"),
  },
  {
    title: "Fog Entry",
    desc: "Make a grand cinematic entrance with our signature fog effect.",
    image: getThreeDImage("Fog", "fog"),
  },
  {
    title: "Kids Celebration",
    desc: "Make kids' events fun and memorable with standees and playful surprises.",
    image: getThreeDImage("Teddy bear", "teddy_bear"),
  },
  {
    title: "Canvas Painting",
    desc: "Transform your favorite photo into a ready-to-display canvas painting.",
    image: getThreeDImage("Framed picture", "framed_picture"),
  },
  {
    title: "Customize Your Song",
    desc: "Add a personalized song that beautifully expresses your story or special message.",
    image: getThreeDImage("Musical notes", "musical_notes"),
  },
  {
    title: "Gaming",
    desc: "Play free games with your celebration or upgrade to premium gaming experiences.",
    image: getThreeDImage("Video game", "video_game"),
  },
];

const formatNumber = (number) => String(number).padStart(2, "0");

const FeatureImage = ({ item }) => (
  <div className="feature-image-wrap" aria-hidden="true">
    <img
      className="feature-image"
      src={item.image}
      alt=""
      loading="lazy"
      decoding="async"
      draggable="false"
    />
  </div>
);

const FeaturesSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] =
    useState(false);

  const pauseTimerRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchCurrentXRef = useRef(0);

  const goTo = useCallback((index) => {
    const normalizedIndex =
      ((index % features.length) + features.length) %
      features.length;

    setCurrentIndex(normalizedIndex);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex(
      (previousIndex) => (previousIndex + 1) % features.length
    );
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(
      (previousIndex) =>
        (previousIndex - 1 + features.length) % features.length
    );
  }, []);

  const pauseCarousel = useCallback(() => {
    window.clearTimeout(pauseTimerRef.current);
    setIsPaused(true);
  }, []);

  const pauseWithResume = useCallback(() => {
    window.clearTimeout(pauseTimerRef.current);
    setIsPaused(true);

    pauseTimerRef.current = window.setTimeout(() => {
      setIsPaused(false);
    }, 5000);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    const updateMotionPreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updateMotionPreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener(
        "change",
        updateMotionPreference
      );
    } else {
      mediaQuery.addListener(updateMotionPreference);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener(
          "change",
          updateMotionPreference
        );
      } else {
        mediaQuery.removeListener(updateMotionPreference);
      }
    };
  }, []);

  useEffect(() => {
    if (isPaused || prefersReducedMotion) {
      return undefined;
    }

    const autoplayTimer = window.setInterval(() => {
      setCurrentIndex(
        (previousIndex) =>
          (previousIndex + 1) % features.length
      );
    }, 4000);

    return () => window.clearInterval(autoplayTimer);
  }, [isPaused, prefersReducedMotion]);

  useEffect(
    () => () => {
      window.clearTimeout(pauseTimerRef.current);
    },
    []
  );

  const handlePrevious = () => {
    goToPrevious();
    pauseWithResume();
  };

  const handleNext = () => {
    goToNext();
    pauseWithResume();
  };

  const handleDotClick = (index) => {
    goTo(index);
    pauseWithResume();
  };

  const handleTouchStart = (event) => {
    const touchPosition = event.targetTouches[0].clientX;

    touchStartXRef.current = touchPosition;
    touchCurrentXRef.current = touchPosition;
    pauseCarousel();
  };

  const handleTouchMove = (event) => {
    touchCurrentXRef.current =
      event.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeDistance =
      touchStartXRef.current - touchCurrentXRef.current;

    if (Math.abs(swipeDistance) > 45) {
      if (swipeDistance > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    pauseWithResume();
  };

  const handleTouchCancel = () => {
    pauseWithResume();
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      handlePrevious();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      handleNext();
    }
  };

  const handleSliderBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      pauseWithResume();
    }
  };

  const progressPercent =
    ((currentIndex + 1) / features.length) * 100;

  return (
    <section
      className="features-section"
      aria-labelledby="features-title"
    >
      <div className="features-decoration features-decoration-one" />
      <div className="features-decoration features-decoration-two" />

      <div className="features-inner">
        <header className="features-header">
          <span className="features-eyebrow">
            EVERYTHING IN ONE PLACE
          </span>

          <h2 className="features-title" id="features-title">
            The HexaPlay Features
          </h2>

          <p className="features-subtitle">
            Everything you need for a memorable private celebration,
            thoughtfully planned around your special occasion.
          </p>
        </header>

        <div
          className="features-grid"
          aria-label="HexaPlay features"
        >
          {features.map((item, index) => (
            <article className="feature-card" key={item.title}>
              <span className="feature-number" aria-hidden="true">
                {formatNumber(index + 1)}
              </span>

              <FeatureImage item={item} />

              <div className="feature-card-content">
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </article>
          ))}
        </div>

        <div
          className="mobile-slider"
          role="region"
          aria-roledescription="carousel"
          aria-label="HexaPlay features"
          onMouseEnter={pauseCarousel}
          onMouseLeave={pauseWithResume}
          onFocusCapture={pauseCarousel}
          onBlurCapture={handleSliderBlur}
        >
          <div className="carousel-status">
            <span
              className="carousel-count"
              aria-live="polite"
              aria-atomic="true"
            >
              <strong>{formatNumber(currentIndex + 1)}</strong>
              <span>/</span>
              <span>{formatNumber(features.length)}</span>
            </span>

            <div
              className="carousel-progress"
              role="progressbar"
              aria-label="Feature carousel progress"
              aria-valuemin="1"
              aria-valuemax={features.length}
              aria-valuenow={currentIndex + 1}
            >
              <span
                className="carousel-progress-fill"
                style={{
                  "--carousel-progress": `${progressPercent}%`,
                }}
              />
            </div>
          </div>

          <div className="carousel-wrapper">
            <button
              type="button"
              className="carousel-arrow carousel-arrow-previous"
              onClick={handlePrevious}
              aria-label="Show previous feature"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M15 19l-7-7 7-7"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div
              className="carousel-viewport"
              tabIndex="0"
              onKeyDown={handleKeyDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
            >
              <div
                className="slider-track"
                style={{
                  "--current-slide": currentIndex,
                }}
              >
                {features.map((item, index) => (
                  <article
                    className="slider-card"
                    key={item.title}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} of ${
                      features.length
                    }: ${item.title}`}
                    aria-hidden={index !== currentIndex}
                  >
                    <span
                      className="slider-card-number"
                      aria-hidden="true"
                    >
                      {formatNumber(index + 1)}
                    </span>

                    <FeatureImage item={item} />

                    <div className="slider-card-content">
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="carousel-arrow carousel-arrow-next"
              onClick={handleNext}
              aria-label="Show next feature"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M9 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div
            className="carousel-dots"
            aria-label="Choose a feature"
          >
            {features.map((item, index) => (
              <button
                type="button"
                key={item.title}
                className={`carousel-dot ${
                  index === currentIndex ? "active" : ""
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Show feature ${index + 1}: ${
                  item.title
                }`}
                aria-current={
                  index === currentIndex ? "true" : undefined
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;